import React, { FC, RefObject } from 'react'
import TextAreaAutosize from 'react-textarea-autosize'
import { Link } from 'react-router-dom'
import { DuplicateIcon, EmojiHappyIcon, RefreshIcon } from '@heroicons/react/outline'
import ROUTES from '../../utils/routes'
import { Button, EmojiPicker } from '../../System'
import { useNotifications } from '../../Notifications'
import { StyledDraftDrawerCaption } from '.'
import { useDraftDrawerCaption } from './useDraftDrawerCaption'

type Props = {
  scrollRef: RefObject<HTMLDivElement>;
}

const DraftDrawerCaption: FC<Props> = ({ scrollRef }) => {
  const { addNotification } = useNotifications()
  const {
    captionText,
    pickerOpen,
    textareaRef,
    actions: {
      handleChange,
      handleEmoji,
      getCharacterCountIcon,
      changeCharacterCountType,
      getCharacterCountLimit,
      setPickerOpen
    }
  } = useDraftDrawerCaption(scrollRef)

  // Copies the caption to the user's clipboard
  const copyCaptionToClipboard = () => {
    navigator.clipboard.writeText(captionText || '')

    if (captionText) {
      addNotification({
        message: 'Caption copied to clipboard.',
        type: 'success',
        interval: 5000
      })
    }
    else {
      addNotification({
        message: 'Please choose a caption.',
        type: 'error',
        interval: 5000
      })
    }
  }

  const handleShuffleCharacterCount = (e: MouseEvent) => {
    const button = e.currentTarget as HTMLButtonElement
    button?.classList.remove('animate')

    window.requestAnimationFrame(() => {
      button?.classList.add('animate')
    })
    changeCharacterCountType()
  }

  return (
    <StyledDraftDrawerCaption htmlFor="draft-drawer-caption" id="draft-drawer-caption-area">
      <div className="textarea-container">
        <TextAreaAutosize
          ref={textareaRef}
          value={captionText || ''}
          onChange={(e) => handleChange(e)}
          id="draft-drawer-caption"
        />
        {!captionText && (
          <span className="textarea-placeholder">
            Start Typing or <Link to={ROUTES.captions}>Choose a Caption</Link>
          </span>
        )}
      </div>
      <div className="textarea-actions">
        {pickerOpen && (
          <EmojiPicker
            onPick={(emoji) => handleEmoji(emoji.native)}
            onClose={() => setPickerOpen(false)}
          />
        )}
        <div className="draft-caption-character-count-container">
          <div className="draft-caption-character-count">
            {getCharacterCountIcon()}
            <span>
              {Array.from(captionText || '').length} / {getCharacterCountLimit()}
            </span>
          </div>
          <Button
            className="switch-character-count-btn"
            isIcon
            onClick={(e) => handleShuffleCharacterCount(e)}
          >
            <RefreshIcon />
          </Button>
        </div>
        <div className="draft-caption-actions">
          <Button
            className="draft-caption-action-btn emoji-picker-btn"
            isIcon
            onClick={() => setPickerOpen(!pickerOpen)}
          >
            <EmojiHappyIcon />
          </Button>
          <Button
            className="draft-caption-action-btn"
            isIcon
            onClick={() => copyCaptionToClipboard()}
          >
            <DuplicateIcon />
          </Button>
        </div>
      </div>
    </StyledDraftDrawerCaption>
  )
}

export default DraftDrawerCaption
